import { Button, Checkbox, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input } from '@1trading/react';
import { useState } from 'react';
import { closeOnly } from '@/components/dialogs/close-only';
import { stableStringify } from '@/utils/object';
import { pluralize } from '@/utils/string';
import { formatFieldValue, parseFieldText, TypeSelect } from './grid-cell';
import { type FieldValue, GRID_FIELDS, type GridField } from './grid-fields';
import type { ProgramRow } from './grid-rows';

interface FieldSummary {
  field: GridField;
  /** Editable selected rows that have the field */
  rows: number;
  current: string;
}

const summarise = (rows: ProgramRow[], field: GridField): FieldSummary => {
  const values = rows.filter((row) => field.appliesTo(row.program, row.instrument)).map((row) => field.get(row.program));
  const distinct = new Set(values.map((value) => stableStringify(value)));
  const current = !values.length ? 'Not on these rows' : distinct.size > 1 ? 'Mixed' : formatFieldValue(field, values[0]) || 'Blank';
  return { field, rows: values.length, current };
};

interface BulkEditDialogProps {
  rows: ProgramRow[];
  onApply: (values: Map<string, FieldValue>) => void;
  onClose: () => void;
}

/** Sets chosen fields to one value on every selected row; fields left unticked keep each row's own value. */
export const BulkEditDialog = ({ rows, onApply, onClose }: BulkEditDialogProps) => {
  const editable = rows.filter((row) => !row.isReadOnly);
  const summaries = GRID_FIELDS.map((field) => summarise(editable, field));
  const [chosen, setChosen] = useState<Map<string, FieldValue>>(new Map());
  const [texts, setTexts] = useState<Record<string, string>>({});

  const choose = (field: GridField, isChosen: boolean) =>
    setChosen((previous) => {
      const next = new Map(previous);
      if (isChosen) next.set(field.key, field.kind === 'boolean' ? false : field.kind === 'type' ? 'least_performing_bucket' : parseFieldText(field, texts[field.key] ?? ''));
      else next.delete(field.key);
      return next;
    });
  const setValue = (field: GridField, value: FieldValue) => setChosen((previous) => new Map(previous).set(field.key, value));
  const setText = (field: GridField, text: string) => {
    setTexts((previous) => ({ ...previous, [field.key]: text }));
    setValue(field, parseFieldText(field, text));
  };
  const groups = [...new Set(GRID_FIELDS.map((field) => field.group))];
  const readOnly = rows.length - editable.length;

  return (
    <Dialog onOpenChange={closeOnly(onClose)} open>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" {...(readOnly ? {} : { 'aria-describedby': undefined })}>
        <DialogHeader>
          <DialogTitle>Edit {pluralize(editable.length, 'schedule')}</DialogTitle>
          {readOnly ? <DialogDescription>{pluralize(readOnly, 'selected schedule')} cannot be edited and will be skipped.</DialogDescription> : null}
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <section className="flex flex-col gap-1" key={group}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group}</h3>
              {summaries
                .filter((summary) => summary.field.group === group)
                .map(({ field, rows: count, current }) => {
                  const isChosen = chosen.has(field.key);
                  const id = `bulk-${field.key}`;
                  return (
                    <div className="grid h-9 grid-cols-[1.25rem_minmax(0,12rem)_minmax(0,9rem)_minmax(0,1fr)] items-center gap-2 text-sm" key={field.key}>
                      <Checkbox checked={isChosen} disabled={!count} id={id} onCheckedChange={(checked) => choose(field, checked === true)} />
                      <label className={count ? '' : 'text-muted-foreground'} htmlFor={id} title={field.description}>
                        {field.longLabel}
                        {field.unit ? <span className="text-muted-foreground"> ({field.unit})</span> : null}
                      </label>
                      <span className="truncate text-xs text-muted-foreground tabular-nums" title={count ? `On ${pluralize(count, 'schedule')}` : undefined}>
                        {current}
                      </span>
                      {!isChosen ? (
                        <span />
                      ) : field.kind === 'boolean' ? (
                        <span className="flex items-center gap-2 text-xs">
                          <Checkbox
                            aria-label={`${field.longLabel} value`}
                            checked={chosen.get(field.key) === true}
                            onCheckedChange={(checked) => setValue(field, checked === true)}
                          />
                          {chosen.get(field.key) === true ? 'On' : 'Off'}
                        </span>
                      ) : field.kind === 'type' ? (
                        <TypeSelect onChange={(value) => setValue(field, value)} value={String(chosen.get(field.key))} />
                      ) : (
                        <Input
                          aria-label={`${field.longLabel} value`}
                          className="h-8 text-xs tabular-nums"
                          onChange={(event) => setText(field, event.target.value)}
                          placeholder={field.kind === 'day' && field.key === 'end_date' ? 'Blank keeps it open' : 'Blank clears it'}
                          type={field.kind === 'day' ? 'date' : 'text'}
                          value={texts[field.key] ?? ''}
                        />
                      )}
                    </div>
                  );
                })}
            </section>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button disabled={!chosen.size || !editable.length} onClick={() => onApply(chosen)} type="button" variant="primary">
            Apply {chosen.size ? pluralize(chosen.size, 'field') : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
BulkEditDialog.displayName = 'BulkEditDialog';
